import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import {
  ArrowLeftOutlined,
  CarOutlined,
  DeliveredProcedureOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  SearchOutlined,
  ShoppingOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getAllNonServiceablePincodes,
  getHubMapListing,
} from "../../services/api/pincodeServiceabilityApi";
import {
  PINCODE_MAP_FILTER_CARD_KEYS,
  PINCODE_MAP_FILTER_PANEL_TEXT,
  PINCODE_MAP_MESSAGES,
  REQUEST_STATUSES,
} from "../../constants/pincodeMapConstants";

const T = {
  bg: "#fafaf8",
  white: "#ffffff",
  border: "#ecebe6",
  text: "#171717",
  muted: "#6b7280",
  soft: "#9ca3af",
  blue: "#2563eb",
  green: "#059669",
  orange: "#ea580c",
  purple: "#7c3aed",
  red: "#dc2626",
  teal: "#0d9488",
};

const INDIA_CENTER = [22.5937, 78.9629];
const DEFAULT_ZOOM = 5;
const PINCODE_FOCUS_ZOOM = 10;
const EXACT_PINCODE_PATTERN = /^\d{6}$/;
const POPUP_OPEN_MAX_RETRIES = 25;
const POPUP_OPEN_RETRY_DELAY_MS = 120;
const TILE_URL =
  "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
const CATEGORY_KEYS = ["courier", "ecomm", "mcn"];
const NUMERIC_QUERY_PATTERN = /^\d+$/;
const PIN_CACHE = {};
const { FULFILLED } = REQUEST_STATUSES;

const CATEGORY_CONFIG = {
  courier: {
    key: "courier",
    label: "Courier",
    mapLabel: "Courier",
    filterLabel: "Courier",
    legendLabel: "Courier",
    statLabel: "Courier",
    tone: "courier",
    Icon: CarOutlined,
  },
  ecomm: {
    key: "ecomm",
    label: "E-commerce",
    mapLabel: "E-com",
    filterLabel: "E-commerce",
    legendLabel: "E-commerce",
    statLabel: "E-commerce",
    tone: "ecomm",
    Icon: ShoppingOutlined,
  },
  mcn: {
    key: "mcn",
    label: "Courier (MCN / 3PL)",
    mapLabel: "MCN",
    filterLabel: "Courier (MCN / 3PL)",
    legendLabel: "Courier (MCN / 3PL)",
    statLabel: "Courier (MCN / 3PL)",
    tone: "mcn",
    Icon: DeliveredProcedureOutlined,
  },
  nonsvc: {
    key: "nonsvc",
    label: "Non Serviceable",
    mapLabel: "Non Svc",
    filterLabel: "Non Serviceable",
    legendLabel: "Non Serviceable",
    statLabel: "Non Serviceable",
    tone: "nonsvc",
    Icon: WarningOutlined,
  },
  hubs: {
    key: "hubs",
    label: "Active Hubs",
    mapLabel: "Hubs",
    filterLabel: "Active Hubs",
    legendLabel: "Hub",
    statLabel: "Active Hubs",
    tone: "hub",
    Icon: HomeOutlined,
  },
};

const toneColor = {
  courier: T.blue,
  ecomm: T.green,
  mcn: T.orange,
  multi: T.purple,
  nonsvc: T.red,
  hub: T.teal,
};

const DEFAULT_FILTERS = {
  courier: true,
  ecomm: true,
  mcn: true,
  nonsvc: false,
  hubs: true,
};

const RESULT_LIMIT = 200;

const popupCardStyle = {
  background: T.white,
  borderRadius: 12,
  border: `1px solid ${T.border}`,
  padding: 14,
  minWidth: 210,
};

const popupHeaderStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 10,
};

const popupMetaStyle = {
  fontSize: 11,
  color: T.soft,
};

const popupMonoStyle = {
  fontSize: 11,
  color: T.soft,
  fontFamily: "JetBrains Mono, monospace",
};

const mapControlsWrapStyle = {
  position: "absolute",
  top: 14,
  right: 14,
  zIndex: 800,
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const mapControlStyle = {
  width: 34,
  height: 34,
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.white,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  cursor: "pointer",
  color: T.muted,
};

const num = (value) => Number(value || 0).toLocaleString("en-IN");
const txt = (value) => (value == null ? "" : String(value).trim());
const time = (value) =>
  value?.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  }) || "";

const n = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const b = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "TRUE";

const pickBool = (item, keys) => keys.some((key) => b(item?.[key]));
const title = (value) =>
  txt(value)
    .toLowerCase()
    .replace(/\b\w/g, (match) => match.toUpperCase());
const contains = (value, query) => txt(value).toLowerCase().includes(query);
const normalizeSearchToken = (value) => txt(value).toLowerCase();
const decodeParam = (value) => {
  if (!value) return "";

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const percentage = (value, total) =>
  `${total ? Math.round((value / total) * 100) : 0}% of total`;

const mapPincode = (item, index) => {
  const courier = pickBool(item, ["courier", "isCourier", "is_courier"]);
  const ecomm = pickBool(item, [
    "ecomm",
    "ecommerce",
    "e_commerce",
    "isEcomm",
  ]);
  const mcn = pickBool(item, ["mcn", "isMcn", "is_mcn", "threePl"]);
  const lat = n(item?.latitude ?? item?.lat);
  const lng = n(item?.longitude ?? item?.lng);

  return {
    id: `${txt(item?.pincode || item?.pinCode)}-${index}`,
    pincode: txt(item?.pincode ?? item?.pinCode ?? item?.pin),
    city: title(item?.city ?? item?.cityName ?? item?.district),
    state: title(item?.state ?? item?.stateName),
    zone: txt(item?.zone ?? item?.zoneName).toUpperCase(),
    courier,
    ecomm,
    mcn,
    lat,
    lng,
    hasCoordinates: lat !== null && lng !== null,
    isNonServiceable: !courier && !ecomm && !mcn,
  };
};

const mapHub = (item, index) => {
  const lat = n(item?.latitude ?? item?.lat);
  const lng = n(item?.longitude ?? item?.lng);

  return {
    id: String(item?.premiseId ?? item?.id ?? `hub-${index}`),
    premiseName: txt(item?.premiseName ?? item?.name),
    city: title(item?.city),
    state: title(item?.state),
    zone: txt(item?.zone).toUpperCase(),
    pincode: txt(item?.pincode),
    lat,
    lng,
    hasCoordinates: lat !== null && lng !== null,
  };
};

const mergePincodeDatasets = (serviceablePins, nonServiceablePins) => {
  const nonServiceableOnly = nonServiceablePins.filter((item) => item.isNonServiceable);
  const mergedByPincode = new Map();

  [...serviceablePins, ...nonServiceableOnly].forEach((item) => {
    if (!item.pincode) return;

    const existing = mergedByPincode.get(item.pincode);
    if (!existing) {
      mergedByPincode.set(item.pincode, item);
      return;
    }

    const courier = existing.courier || item.courier;
    const ecomm = existing.ecomm || item.ecomm;
    const mcn = existing.mcn || item.mcn;
    const lat = existing.lat ?? item.lat;
    const lng = existing.lng ?? item.lng;

    mergedByPincode.set(item.pincode, {
      ...existing,
      courier,
      ecomm,
      mcn,
      lat,
      lng,
      city: existing.city || item.city,
      state: existing.state || item.state,
      zone: existing.zone || item.zone,
      hasCoordinates: lat !== null && lng !== null,
      isNonServiceable: !courier && !ecomm && !mcn,
    });
  });

  return Array.from(mergedByPincode.values());
};

const getPincodeTone = (item) => {
  const activeLines = CATEGORY_KEYS.filter((key) => item[key]).length;
  if (item.isNonServiceable) return "nonsvc";
  if (activeLines > 1) return "multi";
  if (item.courier) return "courier";
  if (item.ecomm) return "ecomm";
  return "mcn";
};

const getResultTagTones = (item) => {
  if (item.isNonServiceable) return [];

  const tones = CATEGORY_KEYS.filter((key) => item[key]);
  return getPincodeTone(item) === "multi" ? [...tones, "multi"] : tones;
};

const matchesQuery = (values, query) =>
  !query || values.some((value) => contains(value, query));

const matchesFilters = (item, filters) => {
  if (item.isNonServiceable) return filters.nonsvc;
  return CATEGORY_KEYS.some((key) => filters[key] && item[key]);
};

const shouldShowHubs = (filters) => filters.hubs;

const buildPincodeStats = (items) =>
  items.reduce(
    (acc, item) => ({
      serviceable: acc.serviceable + (item.isNonServiceable ? 0 : 1),
      courier: acc.courier + (item.courier ? 1 : 0),
      ecomm: acc.ecomm + (item.ecomm ? 1 : 0),
      mcn: acc.mcn + (item.mcn ? 1 : 0),
      nonsvc: acc.nonsvc + (item.isNonServiceable ? 1 : 0),
    }),
    {
      serviceable: 0,
      courier: 0,
      ecomm: 0,
      mcn: 0,
      nonsvc: 0,
    }
  );

const buildStats = (pincodes, hubs) => ({
  ...buildPincodeStats(pincodes),
  hubs: hubs.length,
  zones: new Set(hubs.map((item) => item.zone).filter(Boolean)).size,
});

const buildMeta = (pincodes, mappedPincodes, hubs, mappedHubs) => ({
  pincodeTotal: pincodes.length,
  pincodeMapped: mappedPincodes.length,
  pincodeMissing: pincodes.length - mappedPincodes.length,
  hubTotal: hubs.length,
  hubMapped: mappedHubs.length,
});

const buildStatCards = (stats) => [
  {
    key: "all",
    label: "Total Serviceable",
    value: stats.serviceable,
    sub: "across all lines",
    tone: "multi",
    Icon: EnvironmentOutlined,
  },
  {
    key: "courier",
    label: CATEGORY_CONFIG.courier.statLabel,
    value: stats.courier,
    sub: percentage(stats.courier, stats.serviceable),
    tone: CATEGORY_CONFIG.courier.tone,
    Icon: CATEGORY_CONFIG.courier.Icon,
  },
  {
    key: "ecomm",
    label: CATEGORY_CONFIG.ecomm.statLabel,
    value: stats.ecomm,
    sub: percentage(stats.ecomm, stats.serviceable),
    tone: CATEGORY_CONFIG.ecomm.tone,
    Icon: CATEGORY_CONFIG.ecomm.Icon,
  },
  {
    key: "mcn",
    label: CATEGORY_CONFIG.mcn.statLabel,
    value: stats.mcn,
    sub: percentage(stats.mcn, stats.serviceable),
    tone: CATEGORY_CONFIG.mcn.tone,
    Icon: CATEGORY_CONFIG.mcn.Icon,
  },
  {
    key: "nonsvc",
    label: CATEGORY_CONFIG.nonsvc.statLabel,
    value: stats.nonsvc,
    sub: "no coverage",
    tone: CATEGORY_CONFIG.nonsvc.tone,
    Icon: CATEGORY_CONFIG.nonsvc.Icon,
  },
  {
    key: "hubs",
    label: CATEGORY_CONFIG.hubs.statLabel,
    value: stats.hubs,
    sub: `${num(stats.zones)} active zones`,
    tone: CATEGORY_CONFIG.hubs.tone,
    Icon: CATEGORY_CONFIG.hubs.Icon,
  },
];

const buildMapInfoItems = (stats) => [
  {
    key: "courier",
    label: CATEGORY_CONFIG.courier.mapLabel,
    value: stats.courier,
    tone: CATEGORY_CONFIG.courier.tone,
    Icon: CATEGORY_CONFIG.courier.Icon,
  },
  {
    key: "ecomm",
    label: CATEGORY_CONFIG.ecomm.mapLabel,
    value: stats.ecomm,
    tone: CATEGORY_CONFIG.ecomm.tone,
    Icon: CATEGORY_CONFIG.ecomm.Icon,
  },
  {
    key: "mcn",
    label: CATEGORY_CONFIG.mcn.mapLabel,
    value: stats.mcn,
    tone: CATEGORY_CONFIG.mcn.tone,
    Icon: CATEGORY_CONFIG.mcn.Icon,
  },
  {
    key: "nonsvc",
    label: CATEGORY_CONFIG.nonsvc.mapLabel,
    value: stats.nonsvc,
    tone: CATEGORY_CONFIG.nonsvc.tone,
    Icon: CATEGORY_CONFIG.nonsvc.Icon,
  },
  {
    key: "hubs",
    label: CATEGORY_CONFIG.hubs.mapLabel,
    value: stats.hubs,
    tone: CATEGORY_CONFIG.hubs.tone,
    Icon: CATEGORY_CONFIG.hubs.Icon,
  },
];

const chipStyle = (tone, active) => ({
  padding: "4px 8px",
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 600,
  background: active ? `${toneColor[tone]}15` : "#f5f5f5",
  color: active ? toneColor[tone] : T.soft,
  border: `1px solid ${active ? `${toneColor[tone]}33` : T.border}`,
});

const pinIcon = (tone) => {
  if (!PIN_CACHE[tone]) {
    PIN_CACHE[tone] = L.divIcon({
      className: "",
      html: `<div class="pinmap-pin ${tone}"></div>`,
      iconSize: [14, 14],
      iconAnchor: [7, 7],
      popupAnchor: [0, -10],
    });
  }

  return PIN_CACHE[tone];
};

const hubIcon = L.divIcon({
  className: "",
  html: `
    <div class="pinmap-hub">
      <div class="pinmap-hub-pulse"></div>
      <div class="pinmap-hub-core">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#0d9488"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35z"></path>
          <path d="M6 18h12"></path>
          <path d="M6 14h12"></path>
        </svg>
      </div>
    </div>
  `,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
  popupAnchor: [0, -18],
});



const clusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const allNonServiceable = cluster
    .getAllChildMarkers()
    .every((marker) => marker.options.markerTone === "nonsvc");
  const size = count < 10 ? 34 : count < 100 ? 42 : 50;

  return L.divIcon({
    className: "",
    html: `<div class="pinmap-cluster ${
      allNonServiceable ? "nonsvc" : ""
    }" style="width:${size}px;height:${size}px">${
      count > 999 ? "999+" : count
    }</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const StatCard = ({ item, active, onSelect }) => {
  const { key, label, value, sub, tone, Icon } = item;

  return (
    <button
      type="button"
      className={`pinmap-stat-card ${active ? "pinmap-active" : ""}`}
      onClick={() => onSelect(key)}
      style={{ "--pinmap-tone": toneColor[tone] }}
    >
      <div className="pinmap-stat-label">
        <span className="pinmap-stat-icon">
          <Icon />
        </span>
        {label}
      </div>
      <div className="pinmap-stat-value">{num(value)}</div>
      <div className="pinmap-stat-sub">{sub}</div>
    </button>
  );
};

const ResultRow = ({ item, onFocus }) => {
  const isHub = item.resultType === "hub";

  return (
    <button
      type="button"
      className="pinmap-result-row"
      onClick={() => onFocus(item, isHub ? "hub" : "pincode")}
    >
      <div className="pinmap-result-pin">{isHub ? item.premiseName : item.pincode}</div>
      <div className="pinmap-result-city">
        {[item.city, item.state].filter(Boolean).join(", ") ||
          (isHub ? "Active hub" : "Mapped pincode")}
      </div>
      {isHub ? (
        <span className="pinmap-result-hub">HUB</span>
      ) : item.isNonServiceable ? (
        <span className="pinmap-result-nonsvc">NO SVC</span>
      ) : (
        <div className="pinmap-result-tags">
          {getResultTagTones(item).map((tone) => (
            <span
              key={`${item.id}-${tone}`}
              className="pinmap-result-tag"
              style={{ background: toneColor[tone] }}
            ></span>
          ))}
        </div>
      )}
    </button>
  );
};

const PincodePopup = ({ item }) => {
  const tone = getPincodeTone(item);

  return (
    <div style={popupCardStyle}>
      <div style={popupHeaderStyle}>
        <div
          style={{
            width: 14,
            height: 14,
            borderRadius: 999,
            background: toneColor[tone],
          }}
        ></div>
        <div>
          <div style={{ fontWeight: 700, color: T.text }}>{item.pincode}</div>
          <div style={popupMetaStyle}>
            {[item.city, item.state, item.zone].filter(Boolean).join(" | ") ||
              "Mapped pincode"}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
        {CATEGORY_KEYS.map((key) => (
          <span key={key} style={chipStyle(CATEGORY_CONFIG[key].tone, item[key])}>
            {CATEGORY_CONFIG[key].label}
          </span>
        ))}
        {item.isNonServiceable && (
          <span style={chipStyle(CATEGORY_CONFIG.nonsvc.tone, true)}>
            {CATEGORY_CONFIG.nonsvc.label}
          </span>
        )}
      </div>
      <div style={popupMonoStyle}>
        {item.lat.toFixed(5)}, {item.lng.toFixed(5)}
      </div>
    </div>
  );
};

const HubPopup = ({ item }) => (
  <div style={popupCardStyle}>
    <div style={{ ...popupHeaderStyle, marginBottom: 8 }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          display: "grid",
          placeItems: "center",
          background: `${T.teal}15`,
          color: T.teal,
        }}
      >
        <HomeOutlined />
      </div>
      <div>
        <div style={{ fontWeight: 700, color: T.text }}>{item.premiseName}</div>
        <div style={popupMetaStyle}>
          {[item.city, item.state, item.zone].filter(Boolean).join(" | ") ||
            "Active hub"}
        </div>
      </div>
    </div>
    <div style={{ fontSize: 11, color: T.muted, marginBottom: 8 }}>
      Pincode {item.pincode || "NA"}
    </div>
    <div style={popupMonoStyle}>
      {item.lat.toFixed(5)}, {item.lng.toFixed(5)}
    </div>
  </div>
);

const MapControls = ({ onZoomIn, onZoomOut, onReset }) => (
  <div style={mapControlsWrapStyle}>
    {[
      { key: "zoom-in", label: "+", onClick: onZoomIn },
      { key: "zoom-out", label: "-", onClick: onZoomOut },
      { key: "reset", label: "R", onClick: onReset },
    ].map((control) => (
      <button
        key={control.key}
        type="button"
        onClick={control.onClick}
        style={mapControlStyle}
      >
        {control.label}
      </button>
    ))}
  </div>
);

const MapInfo = ({ items }) => (
  <div className="pinmap-map-info">
    {items.map(({ key, label, value, tone, Icon }) => (
      <span key={key} className="pinmap-map-info-item">
        <Icon style={{ color: toneColor[tone] }} /> {label} <b>{num(value)}</b>
      </span>
    ))}
  </div>
);



const loadingStyle = {
  minHeight: "70vh",
  display: "grid",
  placeItems: "center",
  background: T.bg,
  color: T.muted,
};

const PINMAP_STYLES = `
  .pinmap-page{
    position:relative;background:#f5f8fd;height:calc(100vh - 46px);min-height:760px;overflow:hidden;
    font-family:Roboto,"SF Pro Rounded",-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
    color:${T.text};display:flex;flex-direction:column;padding:24px 28px 0
  }
  .ant-layout-content:has(.pinmap-page) + .ant-layout-footer,
  .ant-layout-content:has(.pinmap-page) + footer{display:none!important}
  .ant-layout-content:has(.pinmap-page){overflow:hidden!important;flex:1;min-height:0;background:#f5f8fd!important;padding-top:46px}
  .pinmap-card-header{height:79px;background:${T.white};border-radius:24px;display:flex;align-items:center;justify-content:space-between;gap:16px;padding:0 24px;margin:0 0 16px;flex-shrink:0}
  .pinmap-card-heading{display:flex;align-items:center;gap:8px;min-width:0}
  .pinmap-card-heading h1{margin:0;color:#003371;font-size:18px;font-weight:600;line-height:28px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .pinmap-back-button{width:24px;height:24px;border:0;background:transparent;color:#175eb6;display:grid;place-items:center;padding:0;cursor:pointer;border-radius:6px;flex-shrink:0}
  .pinmap-back-button:hover{background:#edf5ff;color:#003371}
  .pinmap-last-sync{display:flex;align-items:center;gap:6px;background:#fef3f2;color:#b42318;border-radius:8px;padding:4px 12px;font-size:12px;font-weight:600;white-space:nowrap;flex-shrink:0}
  .pinmap-sync-dot{width:6px;height:6px;border-radius:50%;background:#f04438;animation:pinmapDot 2s infinite}
  .pinmap-sync-dot.refreshing{animation-duration:.9s}
  .pinmap-alert-bar{
    position:absolute;top:10px;left:50%;transform:translateX(-50%);display:flex;align-items:center;gap:10px;
    max-width:min(520px,calc(100% - 48px));padding:14px 20px;background:${T.white};
    border:1px solid #fde68a;border-radius:12px;box-shadow:0 12px 26px rgba(16,24,40,.12);
    font-size:13px;color:#92400e;z-index:980
  }
  .pinmap-alert-count{font-family:JetBrains Mono, monospace;font-weight:600;background:rgba(234,179,8,.15);padding:2px 8px;border-radius:4px}
  .pinmap-alert-close{margin-left:auto;border:none;background:transparent;color:#92400e;font-size:16px;line-height:1;cursor:pointer;padding:0 2px}
  .pinmap-stats-panel{position:relative;margin:0 0 16px;flex-shrink:0}
  .pinmap-stats-reset{position:absolute;right:0;top:-28px;border:none;background:transparent;color:#175eb6;font-size:12px;font-weight:700;cursor:pointer;text-decoration:underline;padding:0;z-index:2}
  .pinmap-stats-reset:hover{color:#003371}
  .pinmap-stats-row{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:24px}
  .pinmap-stat-card{height:104px;background:${T.white};border:1px solid transparent;border-radius:16px;padding:20px 18px;position:relative;overflow:hidden;cursor:pointer;transition:border-color .2s,box-shadow .2s,transform .2s;text-align:left}
  .pinmap-stat-card:hover{transform:translateY(-1px);box-shadow:0 8px 18px rgba(16,24,40,.06)}
  .pinmap-stat-card.pinmap-active{border-color:#1677ff;box-shadow:0 10px 18px rgba(22,119,255,.16),0 0 0 1px rgba(22,119,255,.1)}
  .pinmap-stat-label{font-size:13px;font-weight:700;color:#0d1117;margin-bottom:8px;display:flex;align-items:center;gap:6px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .pinmap-stat-icon{display:none}
  .pinmap-stat-value{font-size:38px;font-weight:800;font-variant-numeric:tabular-nums;letter-spacing:0;line-height:1;color:var(--pinmap-tone)}
  .pinmap-stat-sub{display:none}
  .pinmap-main{display:grid;grid-template-columns:minmax(320px,1fr) minmax(640px,2fr);gap:16px;flex:1;min-height:0;padding-bottom:16px}
  .pinmap-sidebar{background:${T.white};border:none;border-radius:16px;display:flex;flex-direction:column;overflow:hidden;min-height:0;padding:16px}
  .pinmap-sidebar-section{padding:0 0 14px;flex-shrink:0}
  .pinmap-section-title{font-size:13px;font-weight:700;color:#0d1117;margin-bottom:12px;letter-spacing:0;text-transform:none}
  .pinmap-search-box{height:50px;display:flex;align-items:center;gap:10px;background:${T.white};border:1px solid #d0d5dd;border-radius:12px;padding:0 14px;color:#667085}
  .pinmap-search-box:focus-within{border-color:#1677ff;box-shadow:0 0 0 3px rgba(22,119,255,.12)}
  .pinmap-search-box input{border:none;background:none;outline:none;font-size:14px;color:#344054;width:100%;min-width:0}
  .pinmap-search-box input::placeholder{color:#667085}
  .pinmap-search-hint{margin-top:10px;padding:8px 10px;border-radius:8px;border:1px solid #bfdbfe;background:#eff6ff;display:flex;align-items:center;justify-content:space-between;gap:10px;font-size:11px;color:#1e3a8a}
  .pinmap-search-hint button{border:none;background:transparent;color:${T.blue};cursor:pointer;font-size:11px;font-weight:600;padding:0;text-decoration:underline;white-space:nowrap}
  .pinmap-search-hint button:hover{color:#1d4ed8}
  .pinmap-filter-group{display:flex;flex-direction:column;gap:3px}
  .pinmap-filter-item{display:flex;align-items:center;gap:9px;padding:7px 10px;border-radius:7px;cursor:pointer;transition:background .15s;user-select:none;border:none;background:transparent;text-align:left;width:100%;color:${T.text}}
  .pinmap-filter-item:hover{background:#f5f5f2}
  .pinmap-filter-check{width:16px;height:16px;border-radius:4px;border:2px solid rgba(0,0,0,.1);display:grid;place-items:center;transition:all .15s;flex-shrink:0;font-size:10px;font-weight:700;color:#fff}
  .pinmap-filter-check.checked{border-color:transparent;background:var(--pinmap-tone)}
  .pinmap-filter-icon{display:inline-flex;align-items:center;justify-content:center;color:${T.muted}}
  .pinmap-filter-label{font-size:12px;font-weight:500;flex:1}
  .pinmap-filter-count{font-family:JetBrains Mono, monospace;font-size:11px;color:${T.soft};font-weight:500}
  .pinmap-legend-grid{display:grid;grid-template-columns:1fr 1fr;gap:5px}
  .pinmap-legend-item{display:flex;align-items:center;gap:5px;font-size:11px;color:${T.muted}}
  .pinmap-legend-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
  .pinmap-results-header{padding:0 0 14px;display:flex;align-items:center;justify-content:space-between;gap:10px;flex-shrink:0}
  .pinmap-results-title{margin:0}
  .pinmap-results-count{font-size:12px;color:#039855;background:#ecfdf3;border-radius:8px;padding:4px 10px;font-weight:700;white-space:nowrap}
  .pinmap-results-section{flex:1 1 auto;overflow-y:auto;min-height:0;display:grid;grid-template-columns:repeat(4,minmax(0,1fr));grid-auto-rows:30px;align-content:start;gap:10px}
  .pinmap-results-section::-webkit-scrollbar{width:3px}
  .pinmap-results-section::-webkit-scrollbar-thumb{background:rgba(0,0,0,.1);border-radius:2px}
  .pinmap-result-row{
    height:30px;min-width:0;display:flex;align-items:center;justify-content:center;gap:6px;padding:0 8px;
    border:none;border-radius:8px;cursor:pointer;transition:transform .15s,box-shadow .15s,background .15s;
    background:${T.white};box-shadow:0 6px 9px rgba(0,0,0,.06);text-align:center;width:100%
  }
  .pinmap-result-row:hover{background:#f7fbff;transform:translateY(-1px)}
  .pinmap-result-pin{font-size:12px;font-weight:700;color:#344054;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .pinmap-result-city{display:none}
  .pinmap-result-tags{display:flex;gap:4px;flex-shrink:0}
  .pinmap-result-tag{width:8px;height:8px;border-radius:50%}
  .pinmap-result-hub{font-size:9px;font-weight:700;color:${T.teal};background:rgba(13,148,136,.08);padding:1px 5px;border-radius:4px;flex-shrink:0}
  .pinmap-result-nonsvc{font-size:9px;font-weight:700;color:${T.red};background:rgba(220,38,38,.06);padding:1px 5px;border-radius:4px;flex-shrink:0}
  .pinmap-empty{padding:16px;font-size:12px;color:${T.soft}}
  .pinmap-map-wrap{position:relative;background:${T.white};border:none;border-radius:16px;overflow:hidden;min-height:0;height:100%;box-shadow:inset 0 0 0 1px rgba(16,24,40,.03)}
  
  
  .pinmap-map-info{
    position:absolute;left:14px;bottom:14px;z-index:800;display:flex;flex-wrap:wrap;gap:14px;background:${T.white};
    border:1px solid ${T.border};border-radius:10px;padding:10px 14px;box-shadow:0 4px 16px rgba(0,0,0,.08);font-size:11px;color:${T.muted}
  }
  .pinmap-map-info-item{display:flex;align-items:center;gap:5px}
  .pinmap-map-info-item b{font-family:JetBrains Mono, monospace;color:${T.text}}
  .leaflet-popup-content-wrapper{background:transparent!important;box-shadow:none!important;padding:0!important}
  .leaflet-popup-content{margin:0!important}
  .leaflet-popup-tip-container,.leaflet-control-zoom{display:none!important}
  .leaflet-control-attribution{font-size:10px!important;background:rgba(255,255,255,.86)!important}
  .pinmap-pin{width:11px;height:11px;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3)}
  .pinmap-pin.courier{background:${T.blue}}
  .pinmap-pin.ecomm{background:${T.green}}
  .pinmap-pin.mcn{background:${T.orange}}
  .pinmap-pin.multi{background:${T.purple}}
  .pinmap-pin.nonsvc{background:${T.red}}
  .pinmap-hub{width:32px;height:32px;position:relative;display:flex;align-items:center;justify-content:center}
  .pinmap-hub-pulse{position:absolute;width:36px;height:36px;border-radius:50%;background:rgba(13,148,136,.12);animation:pinmapPulse 2.5s ease-out infinite;z-index:1}
  .pinmap-hub-core{width:28px;height:28px;background:#fff;border:2px solid ${T.teal};border-radius:7px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 10px rgba(13,148,136,.35);position:relative;z-index:2}
  .pinmap-cluster{display:flex;align-items:center;justify-content:center;border-radius:50%;color:#fff;font-weight:700;font-family:JetBrains Mono, monospace;border:3px solid #fff;box-shadow:0 2px 12px rgba(0,0,0,.2);background:rgba(37,99,235,.9)}
  .pinmap-cluster.nonsvc{background:rgba(220,38,38,.8)}
  @keyframes pinmapDot{0%,100%{opacity:1}50%{opacity:.3}}
  @keyframes pinmapPulse{0%{transform:scale(.6);opacity:.7}100%{transform:scale(2);opacity:0}}
  @keyframes pinmapTyping{0%,60%,100%{transform:translateY(0);opacity:.4}30%{transform:translateY(-4px);opacity:1}}
  @media (max-width: 1360px){
    .pinmap-page{min-height:900px;overflow:auto}
    .pinmap-stats-row{grid-template-columns:repeat(3,minmax(0,1fr));gap:16px}
    .pinmap-main{grid-template-columns:minmax(320px,1fr) minmax(560px,2fr)}
  }
  @media (max-width: 1100px){
    .pinmap-page{height:auto;min-height:100vh;overflow:visible;padding:16px}
    .pinmap-card-header{height:auto;min-height:72px;border-radius:18px;flex-wrap:wrap;padding:18px}
    .pinmap-main{grid-template-columns:1fr;flex:none;min-height:initial;padding-bottom:16px}
    .pinmap-sidebar{min-height:420px}
    .pinmap-map-wrap{min-height:520px}
  }
  @media (max-width: 600px){
    .pinmap-page{padding:12px}
    .pinmap-card-heading h1{font-size:16px}
    .pinmap-last-sync{font-size:11px}
    .pinmap-stats-row{grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
    .pinmap-stat-card{height:86px;padding:14px}
    .pinmap-stat-label{font-size:11px}
    .pinmap-stat-value{font-size:28px}
    .pinmap-results-section{grid-template-columns:repeat(3,minmax(0,1fr))}
    .pinmap-map-info{right:14px;left:14px}
  }
`;

const PincodeMap = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pincodeQueryParam =
    searchParams.get("pincode");
  const queryPincode = useMemo(
    () => normalizeSearchToken(decodeParam(pincodeQueryParam)),
    [pincodeQueryParam]
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [pincodes, setPincodes] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [showMissingWarning, setShowMissingWarning] = useState(true);

  const [meta, setMeta] = useState({
    pincodeTotal: 0,
    pincodeMapped: 0,
    pincodeMissing: 0,
    hubTotal: 0,
    hubMapped: 0,
  });
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const mapRef = useRef(null);
  
  const markerRefs = useRef({ pincode: new Map(), hub: new Map() });
  const lastAutoFocusedPincodeIdRef = useRef("");

  const markerRef = useCallback(
    (type, id) => (marker) => {
      if (marker) markerRefs.current[type].set(id, marker);
      else markerRefs.current[type].delete(id);
    },
    []
  );

  const resetMapView = useCallback(() => {
    mapRef.current?.setView(INDIA_CENTER, DEFAULT_ZOOM, { animate: true });
  }, []);

  const openPopupWhenReady = useCallback((type, id) => {
    let attempts = 0;

    const tryOpen = () => {
      const marker = markerRefs.current[type].get(id);
      if (marker) {
        marker.openPopup();
        return;
      }
      if (attempts >= POPUP_OPEN_MAX_RETRIES) return;

      attempts += 1;
      window.setTimeout(tryOpen, POPUP_OPEN_RETRY_DELAY_MS);
    };

    tryOpen();
  }, []);

  

  const focus = useCallback(
    (item, type) => {
      if (!mapRef.current) return;

      const zoom = type === "hub" ? 9 : PINCODE_FOCUS_ZOOM;
      const openPopup = () => openPopupWhenReady(type, item.id);

      mapRef.current.once("moveend", openPopup);
      mapRef.current.setView([item.lat, item.lng], zoom, {
        animate: true,
      });
      window.setTimeout(openPopup, 250);
    },
    [openPopupWhenReady]
  );

  const loadData = useCallback(async () => {
    setRefreshing(true);

    const [allPinsResult, hubsResult] = await Promise.allSettled([
      getAllNonServiceablePincodes(),
      getHubMapListing(),
    ]);

    let mappedPincodes = [];
    let mappedHubs = [];
    let allPincodes = [];
    let allHubs = [];

    if (allPinsResult.status === FULFILLED) {
      const allPins = (allPinsResult.value?.data || []).map(mapPincode);
      const serviceablePins = allPins.filter((item) => !item.isNonServiceable);

      allPincodes = mergePincodeDatasets(serviceablePins, allPins);
      mappedPincodes = allPincodes.filter(
        (item) => item.hasCoordinates && item.pincode
      );
      setPincodes(mappedPincodes);
    } else {
      setPincodes([]);
      message.error(PINCODE_MAP_MESSAGES.PINCODE_SERVICEABILITY_LOAD_FAILED);
    }

    if (hubsResult.status === FULFILLED) {
      allHubs = (hubsResult.value?.data || []).map(mapHub);
      mappedHubs = allHubs.filter(
        (item) => item.hasCoordinates && item.premiseName
      );
      setHubs(mappedHubs);
    } else {
      setHubs([]);
      message.error(PINCODE_MAP_MESSAGES.HUB_LIST_LOAD_FAILED);
    }

    setMeta(buildMeta(allPincodes, mappedPincodes, allHubs, mappedHubs));
    setLastRefreshed(new Date());
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSearch(queryPincode);
  }, [queryPincode]);

  useEffect(() => {
    if (meta.pincodeMissing > 0) {
      setShowMissingWarning(true);
    }
  }, [meta.pincodeMissing]);

  const stats = useMemo(() => buildStats(pincodes, hubs), [hubs, pincodes]);
  const statCards = useMemo(() => buildStatCards(stats), [stats]);
  const query = normalizeSearchToken(search);
  const isDefaultFilterState = useMemo(
    () =>
      Object.keys(DEFAULT_FILTERS).every(
        (key) => Boolean(filters[key]) === Boolean(DEFAULT_FILTERS[key])
      ),
    [filters]
  );
  const isNumericQuery = useMemo(
    () => NUMERIC_QUERY_PATTERN.test(query),
    [query]
  );
  const pincodeMatchesForNumericQuery = useMemo(
    () =>
      isNumericQuery
        ? pincodes.filter((item) =>
          !item.isNonServiceable && matchesQuery([item.pincode], query)
        )
        : [],
    [isNumericQuery, pincodes, query]
  );
  const visiblePincodes = useMemo(
    () =>
      pincodes.filter(
        (item) =>
          matchesFilters(item, filters) &&
          matchesQuery([item.pincode, item.city, item.state, item.zone], query)
      ),
    [filters, pincodes, query]
  );

  const visibleHubs = useMemo(
    () =>
      shouldShowHubs(filters)
        ? hubs.filter((item) => {
          const fields = [
            item.premiseName,
            item.city,
            item.state,
            item.zone,
            item.pincode,
          ];
          return matchesQuery(fields, query);
        })
        : [],
    [filters, hubs, query]
  );

  const visibleStats = useMemo(
    () => ({
      ...buildPincodeStats(visiblePincodes),
      hubs: visibleHubs.length,
    }),
    [visibleHubs.length, visiblePincodes]
  );

  const mapInfoItems = useMemo(
    () => buildMapInfoItems(visibleStats),
    [visibleStats]
  );

  const results = useMemo(() => {
    const pincodeResults = visiblePincodes.map((item) => ({
      ...item,
      resultType: "pincode",
    }));
    const hubResults = query
      ? visibleHubs.map((item) => ({
        ...item,
        resultType: "hub",
      }))
      : [];

    return [...pincodeResults, ...hubResults].slice(0, RESULT_LIMIT);
  }, [query, visibleHubs, visiblePincodes]);

  useEffect(() => {
    if (!EXACT_PINCODE_PATTERN.test(query) || !mapRef.current || !isMapReady) {
      lastAutoFocusedPincodeIdRef.current = "";
      return;
    }

    const exactPin = visiblePincodes.find(
      (item) => normalizeSearchToken(item.pincode) === query
    );
    if (!exactPin) return;
    if (lastAutoFocusedPincodeIdRef.current === exactPin.id) return;

    lastAutoFocusedPincodeIdRef.current = exactPin.id;
    focus(exactPin, "pincode");
  }, [focus, isMapReady, query, visiblePincodes]);

  const handleCardSelect = useCallback(
    (key) => {
      if (key === PINCODE_MAP_FILTER_CARD_KEYS.ALL) {
        setFilters(DEFAULT_FILTERS);
        resetMapView();
        return;
      }

      setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
    },
    [resetMapView]
  );

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const commitSearchToQueryParam = useCallback(
    (value) => {
      const nextParams = new URLSearchParams(searchParams);
      const nextPincode = txt(value);

      if (nextPincode) {
        nextParams.set("pincode", nextPincode);
      } else {
        nextParams.delete("pincode");
      }

      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const queryPincodeHiddenByFilters = useMemo(
    () => {
      if (!pincodeMatchesForNumericQuery.length) return false;

      const visibleIds = new Set(visiblePincodes.map((item) => item.id));
      return pincodeMatchesForNumericQuery.some(
        (item) => !visibleIds.has(item.id)
      );
    },
    [pincodeMatchesForNumericQuery, visiblePincodes]
  );
  const hiddenPincodeCount = useMemo(
    () => {
      if (!pincodeMatchesForNumericQuery.length) return 0;

      const visibleIds = new Set(visiblePincodes.map((item) => item.id));
      return pincodeMatchesForNumericQuery.reduce(
        (count, item) => (visibleIds.has(item.id) ? count : count + 1),
        0
      );
    },
    [pincodeMatchesForNumericQuery, visiblePincodes]
  );

  if (loading) {
    return <div style={loadingStyle}>Loading pincode coverage dashboard...</div>;
  }

  return (
    <React.Fragment>
      <div className="pinmap-page">
        <div className="pinmap-card-header">
          <div className="pinmap-card-heading">
            <button
              type="button"
              className="pinmap-back-button"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <ArrowLeftOutlined />
            </button>
            <h1>Pincode Coverage / Intelligence</h1>
          </div>
          <div className="pinmap-last-sync">
            <span
              className={`pinmap-sync-dot ${refreshing ? "refreshing" : ""}`}
            ></span>
            Last updated {lastRefreshed ? `today at ${time(lastRefreshed)}` : "just now"}
          </div>
        </div>

        {showMissingWarning && meta.pincodeMissing > 0 && (
          <div className="pinmap-alert-bar">
            <WarningOutlined />
            <span className="pinmap-alert-count">{num(meta.pincodeMissing)}</span>
            <span>
              pincodes missing lat/lng coordinates {"\u2014"} not rendered on map
            </span>
            <button
              type="button"
              className="pinmap-alert-close"
              aria-label="Close missing pincodes warning"
              onClick={() => setShowMissingWarning(false)}
            >
              x
            </button>
          </div>
        )}

        <div className="pinmap-stats-panel">
          {!isDefaultFilterState && (
            <button type="button" className="pinmap-stats-reset" onClick={clearFilters}>
              {PINCODE_MAP_FILTER_PANEL_TEXT.RESET_BUTTON}
            </button>
          )}
          <div className="pinmap-stats-row">
            {statCards.map((item) => (
              <StatCard
                key={item.key}
                item={item}
                active={
                  item.key === PINCODE_MAP_FILTER_CARD_KEYS.ALL
                    ? isDefaultFilterState
                    : filters[item.key] && !isDefaultFilterState
                }
                onSelect={handleCardSelect}
              />
            ))}
          </div>
        </div>

        <div className="pinmap-main">
          <div className="pinmap-sidebar">
            <div className="pinmap-sidebar-section">
              <div className="pinmap-section-title">Search</div>
              <div className="pinmap-search-box">
                <SearchOutlined />
                <input
                  value={search}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setSearch(nextValue);
                    commitSearchToQueryParam(nextValue);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      commitSearchToQueryParam(event.currentTarget.value);
                    }
                  }}
                  placeholder="Search pincode, city, hub..."
                />
              </div>
              {queryPincodeHiddenByFilters && (
                <div className="pinmap-search-hint">
                  <span>
                    {num(hiddenPincodeCount)} matching pincode
                    {hiddenPincodeCount > 1 ? "s are" : " is"} hidden by
                    current filters.
                  </span>
                  <button type="button" onClick={clearFilters}>
                    Clear filters
                  </button>
                </div>
              )}
            </div>

            <div className="pinmap-results-header">
              <div className="pinmap-section-title pinmap-results-title">
                Mapped Pincode
              </div>
              <div className="pinmap-results-count">
                Showing {num(results.length)}
              </div>
            </div>

            <div className="pinmap-results-section">
              {results.length ? (
                results.map((item) => (
                  <ResultRow key={`${item.resultType}-${item.id}`} item={item} onFocus={focus} />
                ))
              ) : (
                <div className="pinmap-empty">
                  No records match the current filters.
                </div>
              )}
            </div>
          </div>

          <div className="pinmap-map-wrap">
            <MapContainer
              center={INDIA_CENTER}
              zoom={DEFAULT_ZOOM}
              ref={mapRef}
              whenReady={() => setIsMapReady(true)}
              style={{ height: "100%", minHeight: 620, width: "100%" }}
              scrollWheelZoom
            >
              <TileLayer
                attribution={
                  "&copy; <a href=\"https://www.openstreetmap.org/\">OpenStreetMap</a> contributors &copy; CARTO"
                }
                url={TILE_URL}
              />
              <MarkerClusterGroup
                chunkedLoading
                showCoverageOnHover={false}
                spiderfyOnMaxZoom
                zoomToBoundsOnClick
                maxClusterRadius={40}
                disableClusteringAtZoom={10}
                iconCreateFunction={clusterIcon}
              >
                {visiblePincodes.map((item) => {
                  const tone = getPincodeTone(item);

                  return (
                    <Marker
                      key={item.id}
                      position={[item.lat, item.lng]}
                      icon={pinIcon(tone)}
                      markerTone={tone}
                      ref={markerRef("pincode", item.id)}
                    >
                      <Popup closeButton={false} maxWidth={260}>
                        <PincodePopup item={item} />
                      </Popup>
                    </Marker>
                  );
                })}
              </MarkerClusterGroup>

              {visibleHubs.map((item) => (
                <Marker
                  key={item.id}
                  position={[item.lat, item.lng]}
                  icon={hubIcon}
                  ref={markerRef("hub", item.id)}
                >
                  <Popup closeButton={false} maxWidth={260}>
                    <HubPopup item={item} />
                  </Popup>
                </Marker>
              ))}

              
            </MapContainer>

            <MapControls
              onZoomIn={() => mapRef.current?.zoomIn()}
              onZoomOut={() => mapRef.current?.zoomOut()}
              onReset={resetMapView}
            />

            
            <MapInfo items={mapInfoItems} />
          </div>

          
        </div>

        <style>{PINMAP_STYLES}</style>
      </div>
    </React.Fragment>
  );
};

export default PincodeMap;
